import { apiCall, API_BASE_URL } from '../../utils/apiClient';
import i18next from '../../i18n/i18n';
import {
  ActiveTestCaseTemplate,
  BulkTestCaseGenerationJobResponse,
  BulkTestCaseItemStatus,
  BulkTestCaseJobStatus,
  GenerateBulkTestCasesResponse,
  GenerateTestCasesResponse,
  GeneratedTestCaseField,
  GeneratedTestCaseRow,
  RequirementForTestCaseGeneration,
  RequirementGenerationFilter,
  RequirementStatus,
  SubmitTestCaseFeedbackResponse,
  TestCaseInstance,
  TestCaseThread,
  TestCaseThreadStatus,
  TraceabilityRequirement,
  TraceabilityResponse,
  TraceabilitySummary,
} from './types';
import {
  mapTestCaseInstanceFields,
  TestCaseInstanceDto,
} from './instance-review/instanceReviewApi';
import { startTestCaseThread } from './thread-review/threadReviewApi';

interface RequirementContentDto {
  text?: {
    text?: string;
  };
}

interface ThreadDto {
  requirementThreadId: string;
  projectId: string;
  name?: string;
  externalRef?: string;
  threadStatus: string;
  acceptedVersionId?: string;
  updatedAt?: string;
  approvedAt?: string;
  owner?: string;
}

interface InstanceDto {
  requirementVersionId: string;
  requirementThreadId: string;
  content?: RequirementContentDto;
  status: string;
  updatedAt?: string;
}

interface ListThreadsResponseDto {
  threads?: ThreadDto[];
  total?: string;
}

interface GetThreadResponseDto {
  thread: ThreadDto;
  instances?: InstanceDto[];
}

interface SubmitTestCaseFeedbackResponseDto {
  sessionId?: string;
  session_id?: string;
  generatedTestCase?: string;
  generated_test_case?: string;
  instanceVersion?: number;
  instance_version?: number;
  instanceStatus?: string;
  instance_status?: string;
}

interface TestCaseThreadDetailDto {
  threadId?: string | number;
  thread_id?: string | number;

  requirementThreadId?: string | number;
  requirement_thread_id?: string | number;

  currentInstanceId?: string | number;
  current_instance_id?: string | number;

  approvedInstanceId?: string | number;
  approved_instance_id?: string | number;

  currentVersionNumber?: number;
  current_version_number?: number;

  status?: string;

  updatedAt?: string;
  updated_at?: string;
}

interface GetTestCaseThreadResponseDto {
  thread?: TestCaseThreadDetailDto;

  current?: TestCaseInstanceDto;
  approved?: TestCaseInstanceDto;

  instances?: TestCaseInstanceDto[];

  totalInstances?: string | number;
  total_instances?: string | number;

  instancePage?: number;
  instance_page?: number;

  instancePageSize?: number;
  instance_page_size?: number;
}

interface ActiveTemplateFieldDto {
  key?: string;
  label?: string;
  required?: boolean;

  isList?: boolean;
  is_list?: boolean;

  readOnly?: boolean;
  read_only?: boolean;
}

interface GetActiveTemplateFieldsResponseDto {
  source?: string;
  fields?: ActiveTemplateFieldDto[];
}

interface CreateTestCaseInstanceViewDto {
  instanceId?: string | number;
  instance_id?: string | number;

  version?: number;

  text?: string;
  source?: string;

  createdAt?: string;
  created_at?: string;

  testCaseId?: string;
  test_case_id?: string;
}

interface CreateTestCaseResponseDto {
  threadId?: string | number;
  thread_id?: string | number;

  status?: string;

  instance?: CreateTestCaseInstanceViewDto;
}

type GeneratedFieldDto = {
  key?: string;
  name?: string;
  label?: string;
  value?: string | string[];
};

type GeneratedTestCaseDto = {
  id?: string;
  testCaseId?: string;
  title?: string;
  preconditions?: string[] | string;
  steps?: string[] | string;
  expectedResults?: string;
  expected_results?: string;
  traceability?: string;
  priority?: string;
  status?: string;
  requirementThreadId?: string;
  requirement_thread_id?: string;
  requirementVersionId?: string;
  requirement_version_id?: string;
  fields?: GeneratedFieldDto[];
  [key: string]: unknown;
};

interface GeneratedTestCaseReferenceDto {
  testCaseId?: string;
  test_case_id?: string;

  threadId?: string | number;
  thread_id?: string | number;
}

interface GenerateTestCaseResponseDto {
  sessionId?: string;
  session_id?: string;

  generatedTestCase?: string;
  generated_test_case?: string;

  testCases?: GeneratedTestCaseReferenceDto[];
  test_cases?: GeneratedTestCaseReferenceDto[];
}

interface GenerateBulkTestCaseResultDto {
  requirementThreadId?: string | number;
  requirement_thread_id?: string | number;

  acceptedVersionId?: string | number;
  accepted_version_id?: string | number;

  requirementId?: string;
  requirement_id?: string;

  requirementTitle?: string;
  requirement_title?: string;

  externalRef?: string;
  external_ref?: string;

  generationSucceeded?: boolean;
  generation_succeeded?: boolean;

  sessionId?: string;
  session_id?: string;

  generatedTestCase?: string;
  generated_test_case?: string;

  testCases?: GeneratedTestCaseReferenceDto[];
  test_cases?: GeneratedTestCaseReferenceDto[];

  persistenceStatus?: string;
  persistence_status?: string;

  errorCode?: string;
  error_code?: string;

  errorMessage?: string;
  error_message?: string;

  status?: string | number;
}

interface GenerateBulkTestCasesResponseDto {
  jobId?: string;
  job_id?: string;
  status?: string | number;
}

interface GetBulkTestCaseGenerationJobResponseDto {
  jobId?: string;
  job_id?: string;

  status?: string | number;

  total?: number;

  generationSucceeded?: number;
  generation_succeeded?: number;

  generationFailed?: number;
  generation_failed?: number;

  pending?: number;

  persisted?: number;

  persistenceFailed?: number;
  persistence_failed?: number;

  results?: GenerateBulkTestCaseResultDto[];

  errorMessage?: string;
  error_message?: string;
}

interface TraceabilityRequirementDto {
  id: string;
  name: string;
  status: string | number;

  testCaseId?: string;
  test_case_id?: string;

  testCaseTitle?: string;
  test_case_title?: string;

  projectId?: string | number;
  project_id?: string | number;

  instanceId?: string | number;
  instance_id?: string | number;

  requirementStatus?: string;
  requirement_status?: string;

  requirementThreadId?: string | number;
  requirement_thread_id?: string | number;

  testCaseThreadId?: string | number;
  test_case_thread_id?: string | number;

  testCaseThreadStatus?: string;
  test_case_thread_status?: string;
}

interface TraceabilitySummaryDto {
  total: number;
  approved: number;
  draft: number;
  notCovered?: number;
  not_covered?: number;
  coveragePercent?: number;
  coverage_percent?: number;
}

interface TraceabilityResponseDto {
  projectId?: string | number;
  project_id?: string | number;

  generatedAt?: string;
  generated_at?: string;

  summary: TraceabilitySummaryDto;

  requirements?: TraceabilityRequirementDto[];

  totalCount?: number;
  total_count?: number;

  page?: number;

  pageSize?: number;
  page_size?: number;
}

interface ApiErrorPayload {
  reason?: string;
  message?: string;

  error?: {
    reason?: string;
    message?: string;
  };
}

async function createApiError(
  response: Response,
  fallbackMessage: string,
): Promise<Error> {
  const responseText = await response.text();

  let reason = '';
  let serverMessage = '';

  if (responseText) {
    try {
      const payload = JSON.parse(responseText) as ApiErrorPayload;

      reason = payload.reason ?? payload.error?.reason ?? '';

      serverMessage = payload.message ?? payload.error?.message ?? '';
    } catch {
      serverMessage = responseText;
    }
  }

  switch (reason) {
    case 'REQUIREMENT_REFERENCE_REQUIRED':
      return new Error(
        i18next.t('testCaseGeneration.messages.requirementReferenceRequired', {
          ns: 'testCases',
        }),
      );

    case 'AMBIGUOUS_REQUIREMENT_REFERENCE':
      return new Error(
        i18next.t('testCaseGeneration.messages.ambiguousRequirementReference', {
          ns: 'testCases',
        }),
      );

    case 'REQUIREMENT_NOT_FOUND':
      return new Error(
        i18next.t('testCaseGeneration.messages.requirementNotFound', {
          ns: 'testCases',
        }),
      );

    default:
      return new Error(
        serverMessage
          ? `${fallbackMessage}: ${serverMessage}`
          : `${fallbackMessage}: HTTP ${response.status}`,
      );
  }
}

function normalizeThreadStatus(status?: string): RequirementStatus {
  const value = (status ?? '').replace('REQUIREMENT_THREAD_STATUS_', '');

  switch (value) {
    case 'DRAFT':
    case 'WIP':
    case 'APPROVED':
    case 'DELETED':
    case 'SPLIT':
    case 'MERGED':
    case 'PENDING_SPLIT':
    case 'PENDING_MERGE':
      return value;
    default:
      return 'UNKNOWN';
  }
}

function findDisplayInstance(thread: ThreadDto, instances?: InstanceDto[]) {
  return (
    instances?.find(
      instance => instance.requirementVersionId === thread.acceptedVersionId,
    ) ??
    instances?.find(instance => instance.status?.includes('CURRENT')) ??
    instances?.find(instance => instance.status?.includes('APPROVED')) ??
    instances?.[0]
  );
}

function mapThreadDetail(
  thread: ThreadDto,
  instances?: InstanceDto[],
): RequirementForTestCaseGeneration {
  const displayInstance = findDisplayInstance(thread, instances);

  return {
    requirementThreadId: thread.requirementThreadId,
    projectId: thread.projectId,
    name: thread.name ?? '',
    externalRef: thread.externalRef ?? '',
    approvedVersionId: thread.acceptedVersionId ?? '0',
    currentVersionId: displayInstance?.requirementVersionId ?? '0',
    status: normalizeThreadStatus(thread.threadStatus),
    text: displayInstance?.content?.text?.text ?? '',
    updatedAt: thread.updatedAt ?? '',
    approvedAt: thread.approvedAt ?? '',
    owner: thread.owner ?? '',
  };
}

function normalizeGeneratedTestCaseStatus(
  status?: string,
): 'DRAFT' | 'APPROVED' {
  const normalized = (status ?? '')
    .replace('TEST_CASE_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'CURRENT':
    case 'SUPERSEDED':
    case 'DRAFT':
    case 'NOT EXECUTED':
      return 'DRAFT';

    case 'APPROVED':
      return 'APPROVED';

    default:
      return 'DRAFT';
  }
}

function ensureTestCaseIdField(
  fields: GeneratedTestCaseField[],
  testCaseId: string,
): GeneratedTestCaseField[] {
  if (!testCaseId) {
    return fields;
  }

  const containsId = fields.some(field => isTestCaseIdFieldKey(field.key));

  if (containsId) {
    return fields;
  }

  return [
    {
      key: 'id',
      label: 'ID',
      value: testCaseId,
    },
    ...fields,
  ];
}

function mapThreadInstance(
  dto: TestCaseInstanceDto,
  fallbackThreadId: string,
): TestCaseInstance {
  const threadId =
    dto.testCaseThreadId ??
    dto.test_case_thread_id ??
    dto.threadId ??
    dto.thread_id ??
    fallbackThreadId;

  const testCaseId = dto.testCaseId ?? dto.test_case_id ?? '';

  const instanceId = dto.instanceId ?? dto.instance_id;

  if (
    instanceId === undefined ||
    instanceId === null ||
    String(instanceId).trim() === ''
  ) {
    throw new Error('Test case instance is missing instanceId.');
  }

  const versionNumber = dto.versionNumber ?? dto.version_number;

  if (typeof versionNumber !== 'number' || versionNumber <= 0) {
    throw new Error('Test case instance is missing versionNumber.');
  }

  return {
    instanceId: String(instanceId),
    threadId: String(threadId),
    versionNumber,

    fields: ensureTestCaseIdField(mapTestCaseInstanceFields(dto), testCaseId),

    createdAt: dto.createdAt ?? dto.created_at ?? '',

    feedbackInstruction: dto.feedbackInstruction ?? dto.feedback_instruction,

    status: normalizeInstanceStatus(dto.status),
    source: normalizeInstanceSource(dto.source),
  };
}

function serializeTestCaseFields(fields: GeneratedTestCaseField[]): string {
  const persistableFields = fields.filter(
    field => !isTestCaseIdFieldKey(field.key),
  );

  if (persistableFields.length === 0) {
    throw new Error('Test case must contain at least one editable field.');
  }

  const testCase = Object.fromEntries(
    persistableFields.map(field => [field.key, field.value]),
  );

  return JSON.stringify({
    test_cases: [testCase],
  });
}

export async function getRequirementForTestCaseGeneration(
  threadId: string,
): Promise<RequirementForTestCaseGeneration> {
  const response = await apiCall(
    `${API_BASE_URL}/requirements/${encodeURIComponent(
      threadId,
    )}?includeInstances=true`,
    {
      method: 'GET',
    },
  );

  if (!response.ok) {
    const errorText = await response.text();

    throw new Error(
      `Requirement detail load failed: ${response.status} ${errorText}`,
    );
  }

  const data = (await response.json()) as GetThreadResponseDto;

  return mapThreadDetail(data.thread, data.instances);
}

export async function listRequirementsForTestCaseGeneration(
  projectId: number,
  filter: RequirementGenerationFilter,
): Promise<RequirementForTestCaseGeneration[]> {
  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new Error(`Invalid project ID: ${projectId}`);
  }

  const query = new URLSearchParams();

  query.set('projectId', String(projectId));
  query.set('limit', '100');
  query.set('offset', '0');
  query.set('includeTerminal', 'true');

  if (filter === 'APPROVED') {
    query.set('threadStatus', 'REQUIREMENT_THREAD_STATUS_APPROVED');
  }

  const response = await apiCall(
    `${API_BASE_URL}/requirements?${query.toString()}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();

    throw new Error(
      `Requirements load failed: ${response.status} ${errorText}`,
    );
  }

  const data = (await response.json()) as ListThreadsResponseDto;
  const threads = data.threads ?? [];

  return Promise.all(
    threads.map(thread =>
      getRequirementForTestCaseGeneration(thread.requirementThreadId),
    ),
  );
}

function toDisplayLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function normalizeValue(value: unknown): string | string[] {
  if (Array.isArray(value)) {
    return value.map(item =>
      typeof item === 'string' ? item : JSON.stringify(item),
    );
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value === undefined || value === null) {
    return '';
  }

  return JSON.stringify(value);
}

function addField(
  fields: GeneratedTestCaseField[],
  key: string,
  label: string,
  value: unknown,
) {
  const normalized = normalizeValue(value);

  if (
    normalized === '' ||
    (Array.isArray(normalized) && normalized.length === 0)
  ) {
    return;
  }

  fields.push({
    key,
    label,
    value: normalized,
  });
}

function mapGeneratedTestCase(
  source?: GeneratedTestCaseDto,
): GeneratedTestCaseRow | undefined {
  if (!source) {
    return undefined;
  }

  const fields: GeneratedTestCaseField[] = [];

  if (source.fields && source.fields.length > 0) {
    source.fields.forEach((field, index) => {
      const key = field.key ?? field.name ?? `field_${index}`;
      const label = field.label ?? toDisplayLabel(key);

      addField(fields, key, label, field.value);
    });
  } else {
    addField(fields, 'id', 'ID', source.id ?? source.testCaseId);
    addField(fields, 'title', 'Title', source.title);
    addField(fields, 'preconditions', 'Preconditions', source.preconditions);
    addField(fields, 'steps', 'Steps', source.steps);
    addField(
      fields,
      'expectedResults',
      'Expected Results',
      source.expectedResults ?? source.expected_results,
    );
    addField(fields, 'traceability', 'Traceability', source.traceability);
    addField(fields, 'priority', 'Priority', source.priority);
  }

  const id = source.id ?? source.testCaseId;

  if (!id) {
    return undefined;
  }

  return {
    id,
    requirementThreadId:
      source.requirementThreadId ?? source.requirement_thread_id,
    requirementVersionId:
      source.requirementVersionId ?? source.requirement_version_id,
    status: normalizeGeneratedTestCaseStatus(source.status),
    fields,
  };
}

function normalizeTemplateFieldKey(key: string): string {
  return key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

export function isTestCaseIdFieldKey(key: string): boolean {
  const normalizedKey = normalizeTemplateFieldKey(key);

  return normalizedKey === 'id' || normalizedKey === 'testcaseid';
}

function getTemplateFieldValues(
  fields: GeneratedTestCaseField[],
  acceptedKeys: string[],
): string[] {
  const normalizedAcceptedKeys = acceptedKeys.map(normalizeTemplateFieldKey);

  const field = fields.find(item =>
    normalizedAcceptedKeys.includes(normalizeTemplateFieldKey(item.key)),
  );

  if (!field) {
    return [];
  }

  if (Array.isArray(field.value)) {
    return field.value.map(value => value.trim()).filter(Boolean);
  }

  const value = field.value.trim();

  return value ? [value] : [];
}

function toTestCaseFieldValues(fields: GeneratedTestCaseField[]): Array<{
  key: string;
  values: string[];
}> {
  return fields
    .map(field => {
      let values: string[];

      if (Array.isArray(field.value)) {
        values = field.value.map(value => value.trim()).filter(Boolean);
      } else {
        const trimmedValue = field.value.trim();

        values = trimmedValue ? [trimmedValue] : [];
      }

      return {
        key: field.key,
        values,
      };
    })
    .filter(field => field.values.length > 0);
}

export function isTestStepsKey(key: string): boolean {
  const normalized = normalizeTemplateFieldKey(key);

  return normalized === 'teststeps' || normalized === 'steps';
}

export function isExpectedResultKey(key: string): boolean {
  const normalized = normalizeTemplateFieldKey(key);

  return normalized === 'expectedresult' || normalized === 'expectedresults';
}

function splitNumberedItems(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item =>
        typeof item === 'string' ? item.trim() : JSON.stringify(item),
      )
      .filter(Boolean);
  }

  if (typeof value !== 'string') {
    return [];
  }

  const trimmed = value.trim();

  if (!trimmed) {
    return [];
  }

  const matches = Array.from(
    trimmed.matchAll(
      /(?:^|\s)(?:Step\s*)?(\d+)[.:)]\s*([\s\S]*?)(?=(?:\s+(?:Step\s*)?\d+[.:)]\s)|$)/gi,
    ),
  );

  if (matches.length === 0) {
    return [trimmed];
  }

  return matches.map(match => {
    const number = match[1];
    const content = match[2].trim();

    return `Step ${number}: ${content}`;
  });
}

function extractStepNumber(value: string): number | undefined {
  const match = value.match(/(?:Step\s*)?(\d+)[.:)]/i);

  if (!match) {
    return undefined;
  }

  return Number(match[1]);
}

export function createPairedStepFields(
  testCase: Record<string, unknown>,
): GeneratedTestCaseField[] {
  const testStepsEntry = Object.entries(testCase).find(([key]) =>
    isTestStepsKey(key),
  );

  const expectedResultEntry = Object.entries(testCase).find(([key]) =>
    isExpectedResultKey(key),
  );

  const rawSteps = testStepsEntry?.[1];
  const rawExpectedResults = expectedResultEntry?.[1];

  if (
    Array.isArray(rawSteps) &&
    rawSteps.some(item => typeof item === 'object' && item !== null)
  ) {
    const steps: string[] = [];
    const expectedResults: string[] = [];

    rawSteps.forEach((item, index) => {
      if (typeof item !== 'object' || item === null) {
        const stepNumber = index + 1;

        steps.push(`Step ${stepNumber}: ${String(item)}`);

        expectedResults.push(
          `Step ${stepNumber}: Expected result not provided for this step.`,
        );

        return;
      }

      const step = item as {
        step_id?: string;
        step_no?: number;
        step_description?: string;
        action?: string;
        description?: string;
        expected_result?: string;
        expectedResult?: string;
      };

      const stepIdentifier =
        step.step_id ?? `Step ${step.step_no ?? index + 1}`;

      const stepDescription =
        step.step_description ?? step.action ?? step.description ?? '';

      const expectedResult =
        step.expected_result ??
        step.expectedResult ??
        'Expected result not provided for this step.';

      steps.push(`${stepIdentifier}: ${stepDescription}`);

      expectedResults.push(`${stepIdentifier}: ${expectedResult}`);
    });

    return [
      {
        key: 'steps',
        label: 'Test Steps',
        value: steps,
      },
      {
        key: 'expectedResults',
        label: 'Expected Results',
        value: expectedResults,
      },
    ];
  }

  const steps = splitNumberedItems(rawSteps);

  const sourceExpectedResults = splitNumberedItems(rawExpectedResults);

  const expectedResultsByStep = new Map<number, string>();

  sourceExpectedResults.forEach(expectedResult => {
    const stepNumber = extractStepNumber(expectedResult);

    if (stepNumber !== undefined) {
      expectedResultsByStep.set(stepNumber, expectedResult);
    }
  });

  const expectedResults = steps.map((step, index) => {
    const stepNumber = extractStepNumber(step) ?? index + 1;

    return (
      expectedResultsByStep.get(stepNumber) ??
      sourceExpectedResults[index] ??
      `Step ${stepNumber}: Expected result not provided for this step.`
    );
  });

  return [
    {
      key: 'steps',
      label: 'Test Steps',
      value: steps,
    },
    {
      key: 'expectedResults',
      label: 'Expected Results',
      value: expectedResults,
    },
  ];
}

interface GeneratedTestCasePayloadDto {
  generatedTestCase?: string;
  generated_test_case?: string;

  testCase?: GeneratedTestCaseDto;
  test_case?: GeneratedTestCaseDto;
  draft?: GeneratedTestCaseDto;

  testCases?: GeneratedTestCaseDto[];
  test_cases?: GeneratedTestCaseDto[];
  drafts?: GeneratedTestCaseDto[];

  results?: Array<{
    testCase?: GeneratedTestCaseDto;
    test_case?: GeneratedTestCaseDto;
    draft?: GeneratedTestCaseDto;
  }>;
}

function extractGeneratedTestCases(
  data: GeneratedTestCasePayloadDto,
): GeneratedTestCaseRow[] {
  const directList =
    data.testCases ?? data.test_cases ?? data.drafts ?? undefined;

  if (directList && directList.length > 0) {
    return directList
      .map(mapGeneratedTestCase)
      .filter((row): row is GeneratedTestCaseRow => row !== undefined);
  }

  if (data.results && data.results.length > 0) {
    return data.results
      .map(result =>
        mapGeneratedTestCase(
          result.testCase ?? result.test_case ?? result.draft,
        ),
      )
      .filter((row): row is GeneratedTestCaseRow => row !== undefined);
  }

  const single = data.testCase ?? data.test_case ?? data.draft ?? undefined;

  const mappedSingle = mapGeneratedTestCase(single);

  if (mappedSingle) {
    return [mappedSingle];
  }

  const generatedTestCase = data.generatedTestCase ?? data.generated_test_case;

  if (generatedTestCase) {
    try {
      const parsed = JSON.parse(generatedTestCase) as
        | Array<Record<string, unknown>>
        | {
            test_cases?: unknown;
            [key: string]: unknown;
          };

      let parsedTestCases: Array<Record<string, unknown>>;

      if (Array.isArray(parsed)) {
        parsedTestCases = parsed;
      } else if (Array.isArray(parsed.test_cases)) {
        parsedTestCases = parsed.test_cases as Array<Record<string, unknown>>;
      } else if ('test_cases' in parsed) {
        throw new Error(
          'Generated test case payload contains an invalid test_cases value.',
        );
      } else {
        parsedTestCases = [parsed];
      }

      return parsedTestCases.map(testCase => {
        const fields: GeneratedTestCaseField[] = [];

        const pairedStepFields = createPairedStepFields(testCase);

        let pairedFieldsAdded = false;

        Object.entries(testCase).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            return;
          }

          const normalizedKey = normalizeTemplateFieldKey(key);

          if (
            normalizedKey === 'testcasestatus' ||
            normalizedKey === 'status'
          ) {
            return;
          }

          if (isTestStepsKey(key)) {
            if (!pairedFieldsAdded) {
              fields.push(...pairedStepFields);
              pairedFieldsAdded = true;
            }

            return;
          }

          if (isExpectedResultKey(key)) {
            return;
          }

          fields.push({
            key,
            label: toDisplayLabel(key),
            value: normalizeValue(value),
          });
        });

        if (!pairedFieldsAdded) {
          fields.push(...pairedStepFields);
        }

        const testCaseIdValue =
          testCase.test_case_id ??
          testCase.TestCaseID ??
          testCase.testCaseId ??
          testCase.ID ??
          testCase.id;

        if (
          typeof testCaseIdValue !== 'string' ||
          testCaseIdValue.trim() === ''
        ) {
          throw new Error('Generated test case is missing test case ID');
        }

        const testCaseId = testCaseIdValue.trim();

        const statusValue =
          testCase.test_case_status ??
          testCase.testCaseStatus ??
          testCase.Status ??
          testCase.status;

        const status =
          typeof statusValue === 'string' ? statusValue : undefined;

        return {
          id: testCaseId,
          status: status ? normalizeGeneratedTestCaseStatus(status) : 'DRAFT',
          fields,
        };
      });
    } catch {
      throw new Error('Failed to parse generated test case payload.');
    }
  }

  return [];
}

function normalizeBulkJobStatus(
  status?: string | number,
): BulkTestCaseJobStatus {
  if (typeof status === 'number') {
    switch (status) {
      case 1:
        return 'QUEUED';
      case 2:
        return 'RUNNING';
      case 3:
        return 'COMPLETED';
      case 4:
        return 'PARTIALLY_FAILED';
      case 5:
        return 'FAILED';
      default:
        return 'UNKNOWN';
    }
  }

  const normalized = (status ?? '')
    .replace('BULK_TEST_CASE_JOB_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'QUEUED':
    case 'RUNNING':
    case 'COMPLETED':
    case 'PARTIALLY_FAILED':
    case 'FAILED':
      return normalized;

    default:
      return 'UNKNOWN';
  }
}

function normalizeBulkItemStatus(
  status?: string | number,
): BulkTestCaseItemStatus {
  if (typeof status === 'number') {
    switch (status) {
      case 1:
        return 'PENDING';
      case 2:
        return 'RUNNING';
      case 3:
        return 'SUCCEEDED';
      case 4:
        return 'FAILED';
      default:
        return 'UNKNOWN';
    }
  }

  const normalized = (status ?? '')
    .replace('BULK_TEST_CASE_ITEM_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'PENDING':
    case 'RUNNING':
    case 'SUCCEEDED':
    case 'FAILED':
      return normalized;

    default:
      return 'UNKNOWN';
  }
}

function mapBulkGenerationResult(
  result: GenerateBulkTestCaseResultDto,
): BulkTestCaseGenerationJobResponse['results'][number] {
  const requirementThreadId = String(
    result.requirementThreadId ?? result.requirement_thread_id ?? '',
  );

  const acceptedVersionId = String(
    result.acceptedVersionId ?? result.accepted_version_id ?? '',
  );

  const generatedTestCase =
    result.generatedTestCase ?? result.generated_test_case ?? '';

  const references = result.testCases ?? result.test_cases ?? [];

  const threadIdByTestCaseId = new Map<string, string>();

  references.forEach(reference => {
    const testCaseId = reference.testCaseId ?? reference.test_case_id ?? '';

    const rawThreadId = reference.threadId ?? reference.thread_id;

    const testCaseThreadId =
      rawThreadId === undefined || rawThreadId === null
        ? ''
        : String(rawThreadId);

    if (testCaseId && testCaseThreadId) {
      threadIdByTestCaseId.set(testCaseId, testCaseThreadId);
    }
  });

  let testCases: GeneratedTestCaseRow[] = [];

  if (generatedTestCase) {
    try {
      testCases = extractGeneratedTestCases({
        generatedTestCase,
      }).map(testCase => ({
        ...testCase,

        testCaseThreadId:
          threadIdByTestCaseId.get(testCase.id) || testCase.testCaseThreadId,

        requirementThreadId:
          testCase.requirementThreadId ?? requirementThreadId,

        requirementVersionId:
          testCase.requirementVersionId ?? acceptedVersionId,
      }));
    } catch {
      // One malformed generated payload must not prevent the other
      // successful results from being displayed.
      testCases = [];
    }
  }

  return {
    requirementThreadId,
    acceptedVersionId,

    requirementId: result.requirementId ?? result.requirement_id ?? '',

    requirementTitle: result.requirementTitle ?? result.requirement_title ?? '',

    externalRef: result.externalRef ?? result.external_ref ?? '',

    generationSucceeded:
      result.generationSucceeded ?? result.generation_succeeded ?? false,

    sessionId: result.sessionId ?? result.session_id ?? '',

    generatedTestCase,

    persistenceStatus:
      result.persistenceStatus ?? result.persistence_status ?? '',

    errorCode: result.errorCode ?? result.error_code ?? '',

    errorMessage: result.errorMessage ?? result.error_message ?? '',

    status: normalizeBulkItemStatus(result.status),

    testCases,
  };
}

function normalizeTestCaseThreadStatus(
  status?: string,
): TestCaseThread['status'] {
  const normalized = (status ?? '')
    .replace('TEST_CASE_THREAD_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'DRAFT':
    case 'WIP':
    case 'APPROVED':
    case 'DELETED':
      return normalized;

    default:
      return 'DRAFT';
  }
}

function normalizeInstanceStatus(status?: string): TestCaseInstance['status'] {
  const normalized = (status ?? '')
    .replace('TEST_CASE_INSTANCE_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'CURRENT':
    case 'PENDING_REVIEW':
    case 'SUPERSEDED':
    case 'REJECTED':
    case 'DISCARDED':
    case 'APPROVED':
      return normalized;

    default:
      return 'UNKNOWN';
  }
}

function normalizeInstanceSource(source?: string): TestCaseInstance['source'] {
  const normalized = (source ?? '')
    .replace('TEST_CASE_INSTANCE_SOURCE_', '')
    .toUpperCase();

  switch (normalized) {
    case 'USER':
    case 'AGENT':
    case 'AI':
      return normalized;

    default:
      return 'AI';
  }
}

export async function generateTestCases(
  requirements: RequirementForTestCaseGeneration[],
): Promise<GenerateTestCasesResponse> {
  if (requirements.length !== 1) {
    throw new Error('generateTestCases requires exactly one requirement.');
  }

  const requirement = requirements[0];

  const projectId = Number(requirement.projectId);

  const requirementThreadId = Number(requirement.requirementThreadId);

  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new Error('Generate Test Case requires a valid project ID.');
  }

  if (!Number.isInteger(requirementThreadId) || requirementThreadId <= 0) {
    throw new Error(
      'Generate Test Case requires a valid requirement thread ID.',
    );
  }

  const response = await apiCall(
    `${API_BASE_URL}/projects/${projectId}/test-cases/generate`,
    {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },

      body: JSON.stringify({
        project_id: projectId,
        requirement_thread_id: requirementThreadId,
        session_id: crypto.randomUUID(),
      }),
    },
  );

  if (!response.ok) {
    throw await createApiError(response, 'Test Case generation failed');
  }

  const data = (await response.json()) as GenerateTestCaseResponseDto;

  const generatedTestCase = data.generatedTestCase ?? data.generated_test_case;

  const references = data.testCases ?? data.test_cases ?? [];

  const threadIdByTestCaseId = new Map<string, string>();

  references.forEach(reference => {
    const testCaseId = reference.testCaseId ?? reference.test_case_id ?? '';

    const testCaseThreadId = String(
      reference.threadId ?? reference.thread_id ?? '',
    );

    if (testCaseId && testCaseThreadId) {
      threadIdByTestCaseId.set(testCaseId, testCaseThreadId);
    }
  });

  const fallbackRequirementVersionId =
    requirement.approvedVersionId && requirement.approvedVersionId !== '0'
      ? requirement.approvedVersionId
      : requirement.currentVersionId;

  const testCases = extractGeneratedTestCases({
    generatedTestCase,
  }).map(testCase => ({
    ...testCase,

    testCaseThreadId: threadIdByTestCaseId.get(testCase.id) || undefined,

    requirementThreadId: requirement.requirementThreadId,

    requirementVersionId: fallbackRequirementVersionId,
  }));

  if (
    testCases.length > 0 &&
    testCases.some(testCase => !testCase.testCaseThreadId)
  ) {
    throw new Error(
      'Generation succeeded, but the backend did not return the persisted Test Case thread ID.',
    );
  }

  return {
    testCases,

    sessionId: data.sessionId ?? data.session_id,

    generatedTestCase,
  };
}

export async function generateBulkTestCases(
  projectId: number,
  requirementThreadIds: string[],
  batchSize: number,
  signal?: AbortSignal,
): Promise<GenerateBulkTestCasesResponse> {
  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new Error('Bulk generation requires a valid project ID.');
  }
  const numericRequirementIds = requirementThreadIds.map(Number);

  if (
    numericRequirementIds.length === 0 ||
    numericRequirementIds.length > 100 ||
    numericRequirementIds.some(id => !Number.isInteger(id) || id <= 0)
  ) {
    throw new Error(
      'Bulk generation requires between 1 and 100 valid requirement thread IDs.',
    );
  }

  if (!Number.isInteger(batchSize) || batchSize < 1 || batchSize > 100) {
    throw new Error('Bulk-generation batch size must be between 1 and 100.');
  }

  const response = await apiCall(
    `${API_BASE_URL}/projects/${projectId}/test-cases/generate/bulk`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        project_id: projectId,
        requirement_ids: numericRequirementIds,
        batch_size: batchSize,
        additional_context: '',
      }),
      signal,
    },
  );

  if (!response.ok) {
    const errorText = await response.text();

    throw new Error(
      `Bulk generation submission failed: ${response.status} ${errorText}`,
    );
  }

  const data = (await response.json()) as GenerateBulkTestCasesResponseDto;

  const jobId = data.jobId ?? data.job_id ?? '';

  if (!jobId) {
    throw new Error('Bulk generation response did not contain a job ID.');
  }

  return {
    jobId,
    status: normalizeBulkJobStatus(data.status),
  };
}

export async function getBulkTestCaseGenerationJob(
  projectId: number,
  jobId: string,
  signal?: AbortSignal,
): Promise<BulkTestCaseGenerationJobResponse> {
  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new Error('Bulk job retrieval requires a valid project ID.');
  }

  if (!jobId.trim()) {
    throw new Error('Bulk job retrieval requires a job ID.');
  }

  const response = await apiCall(
    `${API_BASE_URL}/projects/${projectId}/test-cases/generate/bulk/${encodeURIComponent(
      jobId,
    )}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal,
    },
  );

  if (!response.ok) {
    const errorText = await response.text();

    throw new Error(
      `Bulk generation status load failed: ${response.status} ${errorText}`,
    );
  }

  const data =
    (await response.json()) as GetBulkTestCaseGenerationJobResponseDto;

  return {
    jobId: data.jobId ?? data.job_id ?? jobId,

    status: normalizeBulkJobStatus(data.status),

    total: data.total ?? 0,

    generationSucceeded:
      data.generationSucceeded ?? data.generation_succeeded ?? 0,

    generationFailed: data.generationFailed ?? data.generation_failed ?? 0,

    pending: data.pending ?? 0,

    persisted: data.persisted ?? 0,

    persistenceFailed: data.persistenceFailed ?? data.persistence_failed ?? 0,

    results: (data.results ?? []).map(mapBulkGenerationResult),

    errorMessage: data.errorMessage ?? data.error_message ?? '',
  };
}

export async function submitTestCaseFeedback(
  projectId: number,
  testCaseThreadId: string,
  testCaseId: string,
  instruction: string,
): Promise<SubmitTestCaseFeedbackResponse> {
  const threadId = Number(testCaseThreadId);
  const feedback = instruction.trim();

  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new Error('Feedback requires a valid project ID.');
  }

  if (!Number.isInteger(threadId) || threadId <= 0) {
    throw new Error('Feedback requires a valid Test Case thread ID.');
  }

  if (!testCaseId.trim()) {
    throw new Error('Feedback requires a Test Case ID.');
  }

  if (!feedback) {
    throw new Error('Feedback instruction is required.');
  }

  const thread = await getTestCaseThread(testCaseThreadId);

  if (thread.status === 'DRAFT') {
    await startTestCaseThread(testCaseThreadId);
  } else if (thread.status !== 'WIP') {
    throw new Error(
      i18next.t('testCaseGeneration.feedback.threadNotEditable', {
        ns: 'testCases',
      }),
    );
  }

  const response = await apiCall(
    `${API_BASE_URL}/projects/${projectId}/test-case-threads/${threadId}/feedback`,
    {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },

      body: JSON.stringify({
        project_id: projectId,
        thread_id: threadId,
        test_case_id: testCaseId,
        feedback,
      }),
    },
  );

  if (!response.ok) {
    throw await createApiError(response, 'Feedback generation failed');
  }

  const data = (await response.json()) as SubmitTestCaseFeedbackResponseDto;

  const generatedTestCase = data.generatedTestCase ?? data.generated_test_case;

  return {
    sessionId: data.sessionId ?? data.session_id,

    generatedTestCase,

    versionNumber: data.instanceVersion ?? data.instance_version,

    instanceStatus: normalizeInstanceStatus(
      data.instanceStatus ?? data.instance_status,
    ),

    testCases: extractGeneratedTestCases({
      generatedTestCase,
    }).map(testCase => ({
      ...testCase,
      testCaseThreadId,
    })),
  };
}

function normalizeCoverageStatus(
  status?: string | number,
): 'APPROVED' | 'DRAFT' | 'NOT_COVERED' {
  if (typeof status === 'number') {
    switch (status) {
      case 2:
        return 'DRAFT';
      case 3:
        return 'APPROVED';
      case 1:
      default:
        return 'NOT_COVERED';
    }
  }

  const normalized = (status ?? '')
    .replace('COVERAGE_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'APPROVED':
      return 'APPROVED';
    case 'DRAFT':
      return 'DRAFT';
    case 'NOT_COVERED':
    case 'UNSPECIFIED':
    default:
      return 'NOT_COVERED';
  }
}

function normalizeTraceabilityThreadStatus(
  status?: string,
): TestCaseThreadStatus | undefined {
  if (!status) {
    return undefined;
  }

  const normalized = status
    .replace('TEST_CASE_THREAD_STATUS_', '')
    .toUpperCase();

  switch (normalized) {
    case 'DRAFT':
    case 'WIP':
    case 'APPROVED':
    case 'DELETED':
      return normalized;
    default:
      return undefined;
  }
}

function normalizeTraceabilityTestCaseId(testCaseId?: string): string {
  const normalized = testCaseId?.trim() ?? '';

  return normalized === '-' ? '' : normalized;
}

function mapTraceabilityRequirement(
  requirement: TraceabilityRequirementDto,
): TraceabilityRequirement {
  const rawRequirementThreadId =
    requirement.requirementThreadId ?? requirement.requirement_thread_id;

  if (
    rawRequirementThreadId === undefined ||
    rawRequirementThreadId === null ||
    String(rawRequirementThreadId).trim() === ''
  ) {
    throw new Error(
      `Traceability requirement ${requirement.id} is missing requirement_thread_id.`,
    );
  }

  const rawTestCaseThreadId =
    requirement.testCaseThreadId ?? requirement.test_case_thread_id;

  const rawInstanceId = requirement.instanceId ?? requirement.instance_id;

  return {
    id: requirement.id,
    requirementThreadId: String(rawRequirementThreadId),
    name: requirement.name,
    status: normalizeCoverageStatus(requirement.status),
    testCaseId: normalizeTraceabilityTestCaseId(
      requirement.testCaseId ?? requirement.test_case_id,
    ),
    testCaseTitle: requirement.testCaseTitle ?? requirement.test_case_title,
    projectId: String(requirement.projectId ?? requirement.project_id ?? ''),
    instanceId:
      rawInstanceId === undefined ||
      rawInstanceId === null ||
      Number(rawInstanceId) <= 0
        ? undefined
        : String(rawInstanceId),
    testCaseThreadId:
      rawTestCaseThreadId === undefined ||
      rawTestCaseThreadId === null ||
      Number(rawTestCaseThreadId) <= 0
        ? undefined
        : String(rawTestCaseThreadId),
    testCaseThreadStatus: normalizeTraceabilityThreadStatus(
      requirement.testCaseThreadStatus ?? requirement.test_case_thread_status,
    ),
    requirementStatus:
      requirement.requirementStatus ?? requirement.requirement_status,
  };
}

export async function getTraceability(
  projectId: number,
): Promise<TraceabilityResponse> {
  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new Error(`Invalid project ID: ${projectId}`);
  }

  const pageSize = 500;
  let page = 1;
  let totalCount = 0;
  let generatedAt = '';
  let summary: TraceabilitySummary | undefined;
  const requirements: TraceabilityRequirement[] = [];

  do {
    const query = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });

    const response = await apiCall(
      `${API_BASE_URL}/projects/${projectId}/traceability?${query.toString()}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();

      throw new Error(`Traceability failed: ${response.status} ${errorText}`);
    }

    const data = (await response.json()) as TraceabilityResponseDto;

    if (!data.summary) {
      throw new Error('Traceability response is missing summary.');
    }

    const pageRequirements = data.requirements ?? [];

    if (page === 1) {
      generatedAt = data.generatedAt ?? data.generated_at ?? '';

      summary = {
        total: data.summary.total,
        approved: data.summary.approved,
        draft: data.summary.draft,
        notCovered: data.summary.notCovered ?? data.summary.not_covered ?? 0,
        coveragePercent:
          data.summary.coveragePercent ?? data.summary.coverage_percent ?? 0,
      };

      totalCount =
        data.totalCount ?? data.total_count ?? pageRequirements.length;
    }

    if (pageRequirements.length === 0) {
      break;
    }

    requirements.push(...pageRequirements.map(mapTraceabilityRequirement));

    if (pageRequirements.length < pageSize) {
      break;
    }

    page += 1;
  } while (requirements.length < totalCount);

  if (!summary) {
    throw new Error('Traceability response is missing summary.');
  }

  return {
    projectId: String(projectId),
    generatedAt,
    summary,
    requirements,
    totalCount,
  };
}

export type ExportFormat = 'markdown' | 'pdf' | 'docx';

interface ExportApiResponse {
  data: string;
  content_type: string;
  filename: string;
}

interface ExportResponse {
  blob: Blob;
  filename: string;
}

function decodeFilename(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export async function exportTestCases(
  projectId: number,
  testCaseThreadIds: string[],
  format: ExportFormat,
): Promise<ExportResponse> {
  if (!Number.isInteger(projectId) || projectId <= 0) {
    throw new Error('Export requires a valid project ID.');
  }

  const threadIds = testCaseThreadIds.map(Number);

  if (
    threadIds.length === 0 ||
    threadIds.length > 50 ||
    threadIds.some(threadId => !Number.isInteger(threadId) || threadId <= 0)
  ) {
    throw new Error(
      'Export requires between 1 and 50 valid Test Case thread IDs.',
    );
  }

  const response = await apiCall(
    `${API_BASE_URL}/projects/${projectId}/test-cases/export`,
    {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },

      body: JSON.stringify({
        project_id: projectId,
        thread_ids: threadIds,
        format,
        template_mode: 'default',
      }),
    },
  );

  if (!response.ok) {
    throw await createApiError(response, 'Export failed');
  }

  const contentType =
    response.headers.get('content-type') ?? 'application/octet-stream';

  if (contentType.includes('application/json')) {
    const result = (await response.json()) as ExportApiResponse;

    const binary = window.atob(result.data);

    const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));

    return {
      blob: new Blob([bytes], {
        type: result.content_type,
      }),
      filename: result.filename,
    };
  }

  const contentDisposition = response.headers.get('content-disposition') ?? '';

  const encodedFilenameMatch = contentDisposition.match(
    /filename\*=UTF-8''([^;]+)/i,
  );

  const fallbackFilenameMatch = contentDisposition.match(
    /filename="?([^";]+)"?/i,
  );

  const encodedFilename = encodedFilenameMatch?.[1]
    ? decodeFilename(encodedFilenameMatch[1])
    : undefined;

  const fallbackFilename = fallbackFilenameMatch?.[1];

  const extensionByFormat: Record<ExportFormat, string> = {
    markdown: 'md',
    pdf: 'pdf',
    docx: 'docx',
  };

  const filename =
    encodedFilename ??
    fallbackFilename ??
    `test-cases.${extensionByFormat[format]}`;

  return {
    blob: await response.blob(),
    filename,
  };
}

export async function createTestCase(
  reqThreadId: string,
  fields: GeneratedTestCaseField[],
): Promise<TestCaseThread> {
  const normalizedReqThreadId = reqThreadId.trim();

  if (!normalizedReqThreadId) {
    throw new Error('Create test case requires a requirement thread ID.');
  }

  if (fields.length === 0) {
    throw new Error('Create test case requires at least one template field.');
  }

  const title =
    getTemplateFieldValues(fields, ['title', 'testCaseTitle'])[0] ?? '';

  const objective = getTemplateFieldValues(fields, ['objective'])[0] ?? '';

  const preconditions = getTemplateFieldValues(fields, [
    'preconditions',
    'precondition',
  ]);

  const steps = getTemplateFieldValues(fields, ['steps', 'testSteps']);

  const expectedResults = getTemplateFieldValues(fields, [
    'expectedResult',
    'expectedResults',
    'expected_result',
    'expected_results',
  ]);

  const priority = getTemplateFieldValues(fields, ['priority'])[0] ?? '';

  const response = await apiCall(
    `${API_BASE_URL}/requirements/${encodeURIComponent(
      normalizedReqThreadId,
    )}/test-case`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        /*
         * Keep the fixed properties for backward compatibility.
         * applyTemplateFields() will use the template-driven fields
         * when fields[] is supplied.
         */
        title,
        objective,
        preconditions,
        steps,
        expected_result: expectedResults.join('\n'),
        priority,

        fields: toTestCaseFieldValues(fields),
      }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();

    throw new Error(`Create test case failed: ${response.status} ${errorText}`);
  }

  const data = (await response.json()) as CreateTestCaseResponseDto;

  const threadId = String(data.threadId ?? data.thread_id ?? '');

  if (!threadId) {
    throw new Error('Create test case response did not contain a thread ID.');
  }

  if (!data.instance) {
    throw new Error(
      'Create test case response did not contain the created instance.',
    );
  }

  const rawInstanceId = data.instance.instanceId ?? data.instance.instance_id;

  if (
    rawInstanceId === undefined ||
    rawInstanceId === null ||
    String(rawInstanceId).trim() === ''
  ) {
    throw new Error(
      'Create test case response did not contain an instance ID.',
    );
  }

  const version = data.instance.version;

  if (typeof version !== 'number' || version <= 0) {
    throw new Error(
      'Create test case response did not contain a valid version.',
    );
  }

  const instanceId = String(rawInstanceId);

  const createdAt = data.instance.createdAt ?? data.instance.created_at ?? '';

  const testCaseId =
    data.instance.testCaseId ?? data.instance.test_case_id ?? '';

  /*
   * Convert the creation response into the same DTO shape used by
   * the existing test-case detail mapper.
   */
  const instanceDto = {
    instanceId,
    instance_id: instanceId,

    testCaseThreadId: threadId,
    test_case_thread_id: threadId,

    threadId,
    thread_id: threadId,

    versionNumber: version,
    version_number: version,

    text: data.instance.text ?? '',

    source: data.instance.source ?? 'USER',
    status: 'CURRENT',

    createdAt,
    created_at: createdAt,

    testCaseId,
    test_case_id: testCaseId,
  } as TestCaseInstanceDto;

  const instance = mapThreadInstance(instanceDto, threadId);

  return {
    threadId,
    requirementThreadId: normalizedReqThreadId,

    status: normalizeTestCaseThreadStatus(data.status ?? 'DRAFT'),

    currentVersionNumber: version,

    updatedAt: createdAt,

    instances: [instance],
  };
}

export async function getTestCaseThread(
  threadId: string,
): Promise<TestCaseThread> {
  const normalizedThreadId = threadId.trim();

  const numericThreadId = Number(normalizedThreadId);

  if (!Number.isInteger(numericThreadId) || numericThreadId <= 0) {
    throw new Error('Test Case thread load requires a valid thread ID.');
  }

  const pageSize = 200;

  let page = 1;

  let firstResponse: GetTestCaseThreadResponseDto | undefined;

  const instanceDtosById = new Map<string, TestCaseInstanceDto>();

  let totalInstances = 0;
  let fetchedInstanceCount = 0;

  for (;;) {
    const query = new URLSearchParams({
      instance_page: String(page),
      instance_page_size: String(pageSize),
    });

    const response = await apiCall(
      `${API_BASE_URL}/test-cases/${encodeURIComponent(
        normalizedThreadId,
      )}?${query.toString()}`,
      {
        method: 'GET',

        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw await createApiError(response, 'Test Case thread load failed');
    }

    const data = (await response.json()) as GetTestCaseThreadResponseDto;

    if (!data.thread) {
      throw new Error('Test Case thread response is missing thread details.');
    }

    if (!firstResponse) {
      firstResponse = data;
    }

    const pageInstances = data.instances ?? [];

    totalInstances = Number(
      data.totalInstances ?? data.total_instances ?? pageInstances.length,
    );

    fetchedInstanceCount += pageInstances.length;

    pageInstances.forEach(instance => {
      const instanceId = instance.instanceId ?? instance.instance_id;

      if (
        instanceId === undefined ||
        instanceId === null ||
        String(instanceId).trim() === ''
      ) {
        throw new Error('Test case instance is missing instanceId.');
      }

      instanceDtosById.set(String(instanceId), instance);
    });

    if (
      pageInstances.length === 0 ||
      pageInstances.length < pageSize ||
      fetchedInstanceCount >= totalInstances
    ) {
      break;
    }

    page += 1;
  }

  if (!firstResponse?.thread) {
    throw new Error('Test Case thread response is missing thread details.');
  }

  const data = firstResponse;
  const thread = firstResponse.thread;

  const mappedThreadId = String(
    thread.threadId ?? thread.thread_id ?? normalizedThreadId,
  );

  const instances = Array.from(instanceDtosById.values()).map(instance =>
    mapThreadInstance(instance, mappedThreadId),
  );

  const currentInstance = data.current
    ? mapThreadInstance(data.current, mappedThreadId)
    : undefined;

  if (
    currentInstance &&
    !instances.some(
      instance => instance.instanceId === currentInstance.instanceId,
    )
  ) {
    instances.unshift(currentInstance);
  }

  return {
    threadId: mappedThreadId,

    requirementThreadId: String(
      thread.requirementThreadId ?? thread.requirement_thread_id ?? '',
    ),

    status: normalizeTestCaseThreadStatus(thread.status),

    currentVersionNumber:
      thread.currentVersionNumber ?? thread.current_version_number ?? 0,

    currentInstanceId: String(
      thread.currentInstanceId ?? thread.current_instance_id ?? '',
    ),

    approvedInstanceId: String(
      thread.approvedInstanceId ?? thread.approved_instance_id ?? '',
    ),

    updatedAt: thread.updatedAt ?? thread.updated_at ?? '',

    instances,
  };
}

export async function startWorking(threadId: string): Promise<TestCaseThread> {
  await startTestCaseThread(threadId);

  return getTestCaseThread(threadId);
}

export async function saveTestCaseInstance(
  threadId: string,
  fields: GeneratedTestCaseField[],
): Promise<TestCaseThread> {
  const normalizedThreadId = threadId.trim();

  if (!normalizedThreadId) {
    throw new Error('Save test case requires a thread ID.');
  }

  const text = serializeTestCaseFields(fields);

  if (text.length > 65536) {
    throw new Error(
      'Test case content exceeds the maximum length of 65536 characters.',
    );
  }

  const response = await apiCall(
    `${API_BASE_URL}/test-cases/${encodeURIComponent(
      normalizedThreadId,
    )}/instances`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        text,
      }),
    },
  );

  if (!response.ok) {
    const errorText = await response.text();

    throw new Error(`Save test case failed: ${response.status} ${errorText}`);
  }

  return getTestCaseThread(normalizedThreadId);
}

export async function getActiveTestCaseTemplate(
  projectId: string | number,
): Promise<ActiveTestCaseTemplate> {
  const numericProjectId = Number(projectId);

  if (!Number.isInteger(numericProjectId) || numericProjectId <= 0) {
    throw new Error(`Invalid project ID: ${projectId}`);
  }

  const response = await apiCall(
    `${API_BASE_URL}/projects/${numericProjectId}/test-case-template/fields`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();

    throw new Error(
      `Active test case template load failed: ${response.status} ${errorText}`,
    );
  }

  const data = (await response.json()) as GetActiveTemplateFieldsResponseDto;

  const fields = (data.fields ?? [])
    .map(field => ({
      key: field.key?.trim() ?? '',
      label: field.label?.trim() || toDisplayLabel(field.key ?? ''),
      required: field.required ?? false,
      isList: field.isList ?? field.is_list ?? false,
      readOnly: field.readOnly ?? field.read_only ?? false,
    }))
    .filter(field => Boolean(field.key));

  return {
    source: data.source ?? '',
    fields,
  };
}
